import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Clapperboard, User, Users, Heart, X, Plus, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Common";

const STEPS = ["Story type", "About them", "Themes & tone", "Who will speak", "Review"];

const TYPES = [
  { key: "solo", label: "Solo", icon: User, desc: "One life story — the family legend, in their own words or remembered by loved ones." },
  { key: "couple", label: "Couple", icon: Heart, desc: "Two voices, one love story — told together." },
  { key: "chorus", label: "Chorus", icon: Users, desc: "A family documentary — many relatives interviewed about one person." },
];
const MODES = [
  { key: "living", label: "Living portrait", desc: "They record their own story — a gift while we still can." },
  { key: "in_memory", label: "In memory", desc: "Family members tell the story of someone who has passed." },
];
const ERAS = [
  { value: "full_life", label: "Their whole life" },
  { value: "childhood_youth", label: "Childhood & youth" },
  { value: "adulthood", label: "Adulthood & family years" },
  { value: "later_years", label: "Later years & legacy" },
];
const LENGTHS = [5, 10, 20, 30];

const inputCls = "w-full h-12 rounded-xl px-4 bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]";

const Field = ({ label, helper, required, children }) => (
  <div>
    <label className="text-sm font-medium">{label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}</label>
    {helper && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 mb-1.5">{helper}</p>}
    <div className={helper ? "" : "mt-1.5"}>{children}</div>
  </div>
);

function Chips({ items, onRemove, testid }) {
  if (!items.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid={testid}>
      {items.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[hsl(var(--secondary))] text-sm">
          {t}
          <button onClick={() => onRemove(i)} className="h-5 w-5 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))]" aria-label="Remove"><X size={12} /></button>
        </span>
      ))}
    </div>
  );
}

export default function StorySetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    type: "solo", mode: "living", honoree_name: "", honoree_second_name: "", relationship: "",
    era: "full_life", desired_length_minutes: 10, culture_faith: "",
    themes_emphasize: [], themes_avoid: [], interviewees: [],
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [empInput, setEmpInput] = useState("");
  const [avoidInput, setAvoidInput] = useState("");
  const [ivName, setIvName] = useState("");
  const [ivRel, setIvRel] = useState("");

  const addChip = (key, val, clear) => { const v = val.trim(); if (!v) return; set(key, [...f[key], v]); clear(""); };
  const addInterviewee = () => { if (!ivName.trim()) return; set("interviewees", [...f.interviewees, { name: ivName.trim(), relationship: ivRel.trim(), email: "" }]); setIvName(""); setIvRel(""); };

  const canNext = () => {
    if (step === 1) return f.honoree_name.trim().length > 0 && (f.type !== "couple" || f.honoree_second_name.trim().length > 0);
    return true;
  };

  const create = async () => {
    setSaving(true);
    try {
      const res = await api.post("/stories", {
        type: f.type, mode: f.mode, honoree_name: f.honoree_name, honoree_second_name: f.honoree_second_name,
        relationship: f.relationship, era: f.era, desired_length_minutes: Number(f.desired_length_minutes),
        themes_emphasize: f.themes_emphasize, themes_avoid: f.themes_avoid, culture_faith: f.culture_faith,
        interviewees: f.interviewees,
      });
      toast.success("Your story studio is ready");
      navigate(`/stories/${res.data.story_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create story");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen pb-28 bg-[hsl(var(--background))]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between"><Logo /><button onClick={() => navigate("/dashboard")} className="text-sm text-[hsl(var(--muted-foreground))] hover:underline">Cancel</button></div>

        <div className="mt-6 flex items-center gap-2" data-testid="story-wizard-progress">
          {STEPS.map((s, i) => <div key={i} className="flex-1"><div className="h-1.5 rounded-full" style={{ background: i <= step ? "hsl(var(--accent))" : "hsl(var(--border))" }} /></div>)}
        </div>
        <p className="mt-3 font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Chapter {["I","II","III","IV","V"][step]} · Step {step + 1} of {STEPS.length}</p>
        <h1 className="font-display text-3xl font-semibold mt-1 flex items-center gap-2"><Clapperboard size={24} className="text-[hsl(var(--accent))]" /> {STEPS[step]}</h1>

        <div className="mt-6">
          {step === 0 && (
            <div className="space-y-6" data-testid="story-wizard-type-step">
              <div>
                <p className="text-sm font-medium mb-2">What kind of story is this?</p>
                <div className="grid gap-3" data-testid="story-wizard-type-radio">
                  {TYPES.map((t) => {
                    const active = f.type === t.key;
                    return (
                      <button key={t.key} data-testid={`story-type-${t.key}`} onClick={() => set("type", t.key)}
                        className="text-left rounded-2xl border-2 p-4 flex items-start gap-3 bg-[hsl(var(--card))] transition-colors"
                        style={{ borderColor: active ? "hsl(var(--accent))" : "hsl(var(--border))" }}>
                        <div className="h-10 w-10 grid place-items-center rounded-xl bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))] shrink-0"><t.icon size={18} /></div>
                        <div><div className="font-semibold">{t.label}</div><div className="text-sm text-[hsl(var(--muted-foreground))]">{t.desc}</div></div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Who is telling it?</p>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="story-wizard-mode-radio">
                  {MODES.map((m) => {
                    const active = f.mode === m.key;
                    return (
                      <button key={m.key} data-testid={`story-mode-${m.key}`} onClick={() => set("mode", m.key)}
                        className="text-left rounded-2xl border-2 p-4 bg-[hsl(var(--card))] transition-colors"
                        style={{ borderColor: active ? "hsl(var(--accent))" : "hsl(var(--border))" }}>
                        <div className="font-semibold">{m.label}</div><div className="text-sm text-[hsl(var(--muted-foreground))]">{m.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="story-wizard-about-step">
              <Field label={f.type === "couple" ? "First person's name" : "Their name"} required>
                <input data-testid="story-honoree-name" value={f.honoree_name} onChange={(e) => set("honoree_name", e.target.value)} placeholder="Arthur Bell" className={inputCls} />
              </Field>
              {f.type === "couple" && (
                <Field label="Second person's name" required>
                  <input data-testid="story-second-name" value={f.honoree_second_name} onChange={(e) => set("honoree_second_name", e.target.value)} placeholder="Rose Bell" className={inputCls} />
                </Field>
              )}
              <Field label="Your relationship to them">
                <input data-testid="story-relationship" value={f.relationship} onChange={(e) => set("relationship", e.target.value)} placeholder="Grandson" className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Era of life to cover">
                  <select data-testid="story-era-select" value={f.era} onChange={(e) => set("era", e.target.value)} className={inputCls}>
                    {ERAS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </Field>
                <Field label="Desired film length">
                  <select data-testid="story-length-select" value={f.desired_length_minutes} onChange={(e) => set("desired_length_minutes", e.target.value)} className={inputCls}>
                    {LENGTHS.map((l) => <option key={l} value={l}>{l} minutes</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="story-wizard-themes-step">
              <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-4 flex items-start gap-2.5">
                <Sparkles size={16} className="text-[hsl(var(--accent))] mt-0.5" />
                <p className="text-sm text-[hsl(var(--muted-foreground))]">The AI interviewer will lean into what you emphasize and will never ask about anything you list to avoid.</p>
              </div>
              <Field label="Themes to emphasize" helper="Add topics the story should celebrate. Press Enter to add.">
                <div className="flex gap-2">
                  <input data-testid="story-emphasize-input" value={empInput} onChange={(e) => setEmpInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChip("themes_emphasize", empInput, setEmpInput))} placeholder="e.g. the family bakery" className={inputCls} />
                  <button onClick={() => addChip("themes_emphasize", empInput, setEmpInput)} className="h-12 px-4 rounded-xl border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"><Plus size={16} /></button>
                </div>
                <Chips items={f.themes_emphasize} onRemove={(i) => set("themes_emphasize", f.themes_emphasize.filter((_, x) => x !== i))} testid="story-emphasize-chips" />
              </Field>
              <Field label="Themes to avoid" helper="Sensitive topics the interviewer should never raise.">
                <div className="flex gap-2">
                  <input data-testid="story-avoid-input" value={avoidInput} onChange={(e) => setAvoidInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChip("themes_avoid", avoidInput, setAvoidInput))} placeholder="e.g. the divorce" className={inputCls} />
                  <button onClick={() => addChip("themes_avoid", avoidInput, setAvoidInput)} className="h-12 px-4 rounded-xl border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"><Plus size={16} /></button>
                </div>
                <Chips items={f.themes_avoid} onRemove={(i) => set("themes_avoid", f.themes_avoid.filter((_, x) => x !== i))} testid="story-avoid-chips" />
              </Field>
              <Field label="Culture, faith or language notes" helper="Optional. Helps the interviewer ask with the right tone.">
                <input data-testid="story-culture-input" value={f.culture_faith} onChange={(e) => set("culture_faith", e.target.value)} placeholder="e.g. Catholic, Telugu-speaking, from Vijayawada" className={inputCls} />
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="story-wizard-interviewees-step">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {f.mode === "living" ? "Add anyone who might help fill in details later. You can always invite more." : "Add the family members and friends who will share memories. Each gets their own gentle interview link — no account needed."}
              </p>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input data-testid="story-interviewee-name" value={ivName} onChange={(e) => setIvName(e.target.value)} placeholder="Name" className={inputCls} />
                <input data-testid="story-interviewee-rel" value={ivRel} onChange={(e) => setIvRel(e.target.value)} placeholder="Relationship" className={inputCls} />
                <button data-testid="story-add-interviewee" onClick={addInterviewee} className="h-12 px-4 rounded-xl border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"><Plus size={16} /></button>
              </div>
              {f.interviewees.length > 0 && (
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] divide-y divide-[hsl(var(--border))]" data-testid="story-interviewee-list">
                  {f.interviewees.map((iv, i) => (
                    <div key={i} className="flex items-center justify-between p-3">
                      <div><span className="font-semibold">{iv.name}</span>{iv.relationship && <span className="text-[hsl(var(--muted-foreground))]"> · {iv.relationship}</span>}</div>
                      <button onClick={() => set("interviewees", f.interviewees.filter((_, x) => x !== i))} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden" data-testid="story-wizard-review-step">
              <div className="h-2 bg-[hsl(var(--letterbox))]" />
              <div className="p-6">
                <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{TYPES.find(t=>t.key===f.type)?.label} · {MODES.find(m=>m.key===f.mode)?.label}</div>
                <div className="font-display text-2xl font-semibold mt-1">{f.type === "couple" && f.honoree_second_name ? `${f.honoree_name} & ${f.honoree_second_name}` : `The Story of ${f.honoree_name || "…"}`}</div>
                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-[hsl(var(--muted-foreground))]">Era</dt><dd className="font-semibold">{ERAS.find(e=>e.value===f.era)?.label}</dd></div>
                  <div><dt className="text-[hsl(var(--muted-foreground))]">Target length</dt><dd className="font-semibold">{f.desired_length_minutes} min</dd></div>
                  <div><dt className="text-[hsl(var(--muted-foreground))]">Emphasize</dt><dd className="font-semibold">{f.themes_emphasize.length || "—"}</dd></div>
                  <div><dt className="text-[hsl(var(--muted-foreground))]">Avoid</dt><dd className="font-semibold">{f.themes_avoid.length || "—"}</dd></div>
                  <div><dt className="text-[hsl(var(--muted-foreground))]">People to interview</dt><dd className="font-semibold">{f.interviewees.length || "—"}</dd></div>
                </dl>
                <p className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">We'll open your studio with 9 life chapters and a gentle AI interviewer ready to begin.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          {step > 0 && <button data-testid="story-wizard-back-button" onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 px-4 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors"><ArrowLeft size={16} /> Back</button>}
          <div className="flex-1" />
          {step < STEPS.length - 1 ? (
            <button data-testid="story-wizard-next-button" disabled={!canNext()} onClick={() => canNext() && setStep((s) => s + 1)}
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">Continue <ArrowRight size={16} /></button>
          ) : (
            <button data-testid="story-wizard-create-button" disabled={saving} onClick={create}
              className="inline-flex items-center gap-1.5 px-6 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"><Check size={16} /> {saving ? "Creating…" : "Open my story studio"}</button>
          )}
        </div>
      </div>
    </div>
  );
}
